import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { useFeaturedProducts } from "@/hooks/useProducts";
import {
  getProductFinalPrice,
  getProductImage,
  type DbProduct,
} from "@/lib/supabase";

export default function FeaturedSpotlight() {
  const { data: products, loading, error } = useFeaturedProducts(6);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [fade, setFade] = useState(true);

  // Set the first product as active once data loads
  useEffect(() => {
    if (products.length > 0 && activeId == null) {
      setActiveId(products[0].id);
    }
  }, [products, activeId]);

  const active: DbProduct | undefined = products.find((p) => p.id === activeId);

  const handleSelect = (id: number) => {
    if (id === activeId) return;
    setFade(false);
    // small delay so the cross-fade reads
    setTimeout(() => {
      setActiveId(id);
      setFade(true);
    }, 180);
  };

  return (
    <section
      className="px-4 md:px-10 lg:px-14 py-20 md:py-28"
      data-product-shop
    >
      <Reveal from="up" className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-10 md:mb-14">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-foreground-600 mb-3">
            This season
          </p>
          <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
            Featured pieces
          </h2>
        </div>
        <Link
          to="/#collections"
          className="text-xs md:text-sm text-foreground-700 hover:text-foreground-950 underline underline-offset-4 cursor-pointer whitespace-nowrap group"
        >
          Browse the full shop{" "}
          <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
        </Link>
      </Reveal>

      {error && (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-700 mb-3">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-foreground-950 text-background-50 text-sm cursor-pointer whitespace-nowrap"
          >
            Try again
          </button>
        </div>
      )}

      {!error && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
          {/* Big stage image */}
          <div className="lg:col-span-8">
            <div className="relative w-full aspect-[4/5] md:aspect-[5/4] lg:aspect-[6/5] rounded-lg overflow-hidden bg-background-100">
              {loading && (
                <div className="absolute inset-0 bg-background-200/70 animate-pulse" />
              )}
              {active && (
                <Link
                  to={`/product/${active.id}`}
                  className="absolute inset-0 block cursor-pointer group"
                >
                  <img
                    src={getProductImage(active)}
                    alt={active.name}
                    title={`${active.name} — Hearth & Heirloom`}
                    className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 ease-out ${
                      fade
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-[1.03]"
                    }`}
                  />
                  {/* Bottom info overlay */}
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-8 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                    <div
                      className={`transition-all duration-500 ${
                        fade ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
                      }`}
                    >
                      <p className="text-background-50/80 text-[10px] tracking-[0.3em] uppercase mb-2">
                        {active.product_categories?.name ?? "Heirloom"}
                      </p>
                      <div className="flex items-end justify-between gap-4 flex-wrap">
                        <h3 className="font-heading text-background-50 text-2xl md:text-4xl leading-tight">
                          {active.name}
                        </h3>
                        <div className="flex items-center gap-4">
                          <span className="text-background-50 text-base md:text-lg">
                            ${getProductFinalPrice(active).toFixed(2)}
                          </span>
                          <span className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-background-50 text-foreground-950 text-xs md:text-sm group-hover:bg-background-100 transition-colors whitespace-nowrap">
                            View piece
                            <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              )}
            </div>
          </div>

          {/* Thumbnail rail */}
          <div className="lg:col-span-4">
            <ul className="grid grid-cols-3 lg:grid-cols-2 gap-3 md:gap-4">
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <li
                    key={i}
                    className="aspect-square rounded-md bg-background-200/70 animate-pulse"
                  />
                ))}
              {!loading &&
                products.map((p) => {
                  const isActive = p.id === activeId;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(p.id)}
                        onMouseEnter={() => handleSelect(p.id)}
                        className={`relative w-full aspect-square overflow-hidden rounded-md bg-background-100 cursor-pointer transition-all ${
                          isActive
                            ? "ring-2 ring-foreground-950 ring-offset-2 ring-offset-background-50"
                            : "opacity-70 hover:opacity-100"
                        }`}
                        aria-label={`Preview ${p.name}`}
                      >
                        <img
                          src={getProductImage(p)}
                          alt={p.name}
                          className="absolute inset-0 w-full h-full object-cover object-top"
                        />
                      </button>
                    </li>
                  );
                })}
            </ul>
            <p className="mt-4 text-xs text-foreground-600 hidden lg:block">
              Hover or tap a piece to preview &middot; click the canvas to open
            </p>
          </div>
        </div>
      )}
    </section>
  );
}